import type { DynamicFormSettings } from '@myosh/odin-components';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { type FormBuilderField, selectSelectedField } from '../../../redux/slices/form-builder-slice';
import { AIFieldFormSettings } from '../fields/ai-field/ai-field-form.util';
import { emailThisButtonFormSettings } from '../fields/email-this-button/email-this-button.util';
import { labelFieldFormSettings } from '../fields/label/label-field-form.util';
import { linkFieldFormSettings } from '../fields/link/link-field-form.util';
import { riskRatingFormSettings } from '../fields/risk-rating/risk-rating-field-form.util';
import { textAreaFieldFormSettings } from '../fields/textarea/textarea-field-form.util';
import { getTextualQuestionnaireFieldSettings } from '../fields/textual-questionnaire-field/textual-questionnaire-field-form.utils';
import { timeFieldFormSettings } from '../fields/timefield/time-field-form.util';
import { CommonFieldDetailsFormSettings } from '../util/field-details.util';

const getFieldExtraSettings = (selectedField: FormBuilderField) => {
  switch (selectedField?.fieldType) {
    case 'AI_FIELD':
      return AIFieldFormSettings;
    case 'EMAIL_THIS_BUTTON':
      return emailThisButtonFormSettings;
    case 'LABEL':
      return labelFieldFormSettings;
    case 'LINK':
      return linkFieldFormSettings;
    case 'TEXTAREA':
      return textAreaFieldFormSettings;
    case 'TEXTUAL_QUESTIONNAIRE': {
      return getTextualQuestionnaireFieldSettings(selectedField);
    }
    case 'TIMEFIELD':
      return timeFieldFormSettings;
    case 'RISKRATING':
      return riskRatingFormSettings;
    default:
      return null;
  }
};

const useFormBuilderDetailsSettings = () => {
  const selectedField = useAppSelector(selectSelectedField);

  const [formSettings, setFormSettings] = useState<DynamicFormSettings>();

  useEffect(() => {
    if (selectedField) {
      const extraSettings = getFieldExtraSettings(selectedField);
      const fields = [...CommonFieldDetailsFormSettings.fields, ...(extraSettings?.fields ?? [])].sort(
        (a, b) => a.position - b.position
      );
      const visibilityConditions = extraSettings?.visibilityConditions ?? [];

      setFormSettings({
        ...CommonFieldDetailsFormSettings,
        fields,
        visibilityConditions,
        fieldValueConverters: {
          ...commonSettings.fieldValueConverters,
          ...extraSettings?.fieldValueConverters,
        },
        customFieldComponents: {
          ...commonSettings.customFieldComponents,
          ...extraSettings?.customFieldComponents,
        },
      });
    }
  }, [selectedField, isSuperUser]);

  return formSettings;
};

export default useFormBuilderDetailsSettings;
